// Case5Page.js
import React, { useEffect, useState, useRef } from 'react';
import { PencilIcon, BoltIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import case5Image from '../assets/RedRockCases/Case5/Case 5 placeholder.png';
import SEO from '../components/SEO';

const TIMER_WARNING_THRESHOLD = 0.8; // 80% of the way through

const Case5Page = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [reveal, setReveal] = useState({});
  const [timeExpired, setTimeExpired] = useState(false);
  const [finishedEarly, setFinishedEarly] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [notebookItems, setNotebookItems] = useState([]);
  const [isNotebookOpen, setIsNotebookOpen] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const timerRef = useRef(null);
  const [currentExhibit, setCurrentExhibit] = useState('1');

  const questions = [
    {
      id: 1,
      text: "According to the pie chart, what percentage of Cafe Bloom's orders were placed digitally?",
      answer: "40%",
      type: "text",
      hint: "Look at the order types breakdown."
    },
    {
      id: 2,
      text: "At what time is the average wait time the shortest during the morning rush?",
      answer: "9 AM",
      type: "text",
      hint: "Check the wait time by hour chart."
    },
    {
      id: 3,
      text: "Cafe Bloom's revenue increased from $5,200 in January to $6,700 in March. What is the percentage increase?",
      answer: "28.8%",
      type: "text",
      hint: "Calculate the percentage increase in revenue."
    },
    {
      id: 4,
      text: "Which day had the highest total workload (staff hours + orders served)?",
      answer: "Friday",
      type: "text",
      hint: "Look at the staff and orders data."
    },
    {
      id: 5,
      text: "Which breakfast item was most popular according to the chart?",
      answer: "Muffin",
      type: "text",
      hint: "Check the breakfast items sales data."
    },
    {
      id: 6,
      text: "Which satisfaction factor received the highest score from customers?",
      answer: "Freshness",
      type: "text",
      hint: "Look at the customer satisfaction scores."
    },
    {
      id: 7,
      text: "Which day had the lowest customer count?",
      answer: "Monday",
      type: "text",
      hint: "Check the daily customer traffic data."
    },
    {
      id: 8,
      text: "How many more muffins than bagels were sold?",
      answer: "25",
      type: "text",
      hint: "Compare the sales numbers of muffins and bagels."
    }
  ];

  useEffect(() => {
    if (!hasStarted || timeExpired || finishedEarly) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasStarted, timeExpired, finishedEarly]);

  useEffect(() => {
    // Check if all questions are answered
    const allAnswered = hasStarted && !isComplete && Object.keys(feedback).length === 8;
    
    if (allAnswered) {
      setIsComplete(true);
      // If completed and time is still left, mark as finished early
      if (timeLeft > 0) {
        setFinishedEarly(true);
        // Optional: Stop the timer if finished early
        // clearInterval(timerRef.current);
      }
    }
    // Added questionData.length, timeLeft, and setFinishedEarly to dependencies
  }, [feedback, hasStarted, isComplete, timeLeft, setFinishedEarly]);

  const handleInput = (id, value, partIndex = null) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    if (partIndex !== null) {
      setAnswers((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [partIndex]: value }
      }));
    } else {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const q = questions.find((q) => q.id === id);
    
    if (q.type === 'multi') {
      const allPartsCorrect = q.parts.every((part, index) => {
        const userAnswer = ((answers[id] || {})[index] || '').trim().toLowerCase();
        return userAnswer === part.correctAnswer.toLowerCase();
      });
      setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
    } else {
    const userAnswer = (answers[id] || '').trim().toLowerCase();
    const correctAnswer = q.correctAnswer.toLowerCase();
    setFeedback((prev) => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
    }
  };

  const handleTryAgain = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: '' }));
    setFeedback((prev) => ({ ...prev, [id]: null }));
    setReveal((prev) => ({ ...prev, [id]: false }));
  };

  const handleReveal = (id) => {
    setReveal((prev) => ({ ...prev, [id]: true }));
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Report scoring variables (dynamic scorecard)
    const reportAnswers = {
      'report_digital_percent': '40',
      'report_peak_time': '9',
      'report_revenue_growth': '28.8',
      'report_productive_day': 'Friday',
      'report_muffins': '70',
      'report_satisfaction': 'Freshness',
      'report_lowest_day': 'Monday'
    };
  const numReportQuestions = Object.keys(reportAnswers).length;
  const numAnalysisQuestions = questions.length;
  const numReportCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && k.startsWith('report_')).length;
  const numAnalysisCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && !k.startsWith('report_')).length;
  const totalQuestions = numReportQuestions + numAnalysisQuestions;
  const totalCorrect = numReportCorrect + numAnalysisCorrect;

  const handleReportSubmit = () => {
    if (timeExpired || !hasStarted || finishedEarly) return;

    const newFeedback = {};
    Object.entries(reportAnswers).forEach(([key, correctAnswer]) => {
      const userAnswer = (answers[key] || '').trim();
      newFeedback[key] = userAnswer.toLowerCase() === correctAnswer.toLowerCase() ? 'correct' : 'incorrect';
    });

    setFeedback(prev => ({ ...prev, ...newFeedback, report: 'submitted' }));
    setFinishedEarly(true);
    setTimeExpired(true);
    setReveal(prev => ({ ...prev, report: true }));
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleReportReveal = () => {
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleDragStart = (e, value, label) => {
    if (e.target.closest('.notebook-content')) return;
    const data = {
      value: value,
      label: label
    };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
  };

  const handleDrop = (e) => {
    if (!hasStarted) return;
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const parsedData = JSON.parse(data);
      if (parsedData.value && parsedData.label) {
        setNotebookItems(prev => [...prev, parsedData]);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    e.currentTarget.classList.remove('bg-blue-50');
  };

  const removeNotebookItem = (index) => {
    setNotebookItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNotebookToggle = () => {
    if (!isNotebookOpen) {
      setHasBeenOpened(true);
    }
    setIsNotebookOpen(!isNotebookOpen);
  };

  return (
    <div className="bg-white text-gray-900 font-sans relative scroll-pt-[400px] scroll-smooth">
      <SEO noIndex={true} />
      {/* Mobile Message - Only visible on mobile */}
      <div className="md:hidden min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-blue-200 to-white">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Desktop Required</h1>
          <p className="text-gray-700 max-w-sm mx-auto">
            Cases are designed for desktop and laptop computers. Please use a larger screen to access this case study.
          </p>
          <div className="pt-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg text-blue-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">Best viewed on screens wider than 768px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Content - Hidden on mobile */}
      <div className="hidden md:block">
        {/* Side Navigation */}
        <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999]">
          <div className="group relative">
          <a 
            href="#problem" 
              className="flex items-center"
            aria-label="Problem Section"
          >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Problem
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <PencilIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>

          <div className="group relative">
            <a 
              href="#investigation" 
              className="flex items-center"
              aria-label="Investigation Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Investigation
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <BoltIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>

          <div className="group relative">
            <a 
              href="#exhibits" 
              className="flex items-center"
              aria-label="Exhibits Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Exhibits
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#questions" 
              className="flex items-center"
              aria-label="Analysis Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Analysis
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <ChartBarIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>

          <div className="group relative">
          <a 
            href="#recommendation" 
              className="flex items-center"
            aria-label="Recommendation Section"
          >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Recommendation
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <LightBulbIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>
        </div>

        {/* Hero Section - Enhanced gradient */}
        <section className="sticky top-[50px] z-30 bg-gradient-to-br from-blue-200 to-white pb-4 relative">
          <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 pt-16">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Case 5: Cafe Bloom - Morning Rush Makeover</h1>
              <p className="text-base md:text-lg text-gray-700">Analyze Cafe Bloom's recent changes to improve service efficiency and customer satisfaction. Evaluate the impact of digital ordering, staffing adjustments, and menu changes to recommend the best path forward.</p>
              <div className="mt-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  For the best experience, adjust your browser zoom to 75-80%. This ensures all case elements display properly and are easy to interact with.
                </p>
              </div>
              {(!hasStarted || timeExpired || finishedEarly) && (
                <button
                  onClick={() => {
                    setHasStarted(true);
                    setTimeLeft(1200);
                    setAnswers({});
                    setFeedback({});
                    setReveal({});
                    setTimeExpired(false);
                    setFinishedEarly(false);
                    setIsComplete(false);
                  }}
                  className="group relative mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                >
                  {!hasStarted ? 'Start Case' : 'Restart Case'}
                  <div className="absolute top-full mt-2 left-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-auto">
                    {!hasStarted ? 'Click to unlock the questions and start your case timer.' : 'Click to start a new attempt.'}
                  </div>
                </button>
              )}
            </div>
          </div>
          <div className="absolute top-[85px] right-10 md:right-20 w-[300px] h-[400px] z-10 hidden md:block">
            {hasStarted && (timeLeft === 0 || isComplete) && (
              <div className={`absolute top-0 -left-[250px] bg-white/90 backdrop-blur-sm rounded-lg p-4 animate-pulse w-[220px] ${
                Math.round((totalCorrect / totalQuestions) * 100) < 65 
                ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
                : 'shadow-[0_0_15px_rgba(59,130,246,0.2)]'
              }`}>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r rounded-lg ${
                    Math.round((totalCorrect / totalQuestions) * 100) < 65
                    ? 'from-red-50/50 to-white/50'
                    : 'from-blue-50/50 to-white/50'
                  }`}></div>
                  <div className="relative space-y-2.5">
                    <h2 className="text-[16px] font-semibold text-gray-800 flex items-center gap-1.5">
                      <span className="text-lg">📊</span> Final Score
                    </h2>
                    <p className="text-[15px] text-gray-700">
                      Analysis: {numAnalysisCorrect}/{numAnalysisQuestions}
                    </p>
                    <p className="text-[15px] text-gray-700">
                      Report: {numReportCorrect}/{numReportQuestions}
                    </p>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className={`rounded-full h-2 transition-all duration-300 ${
                          Math.round((totalCorrect / totalQuestions) * 100) < 65
                          ? 'bg-red-500'
                          : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.round((totalCorrect / totalQuestions) * 100)}%` }}
                      ></div>
                    </div>
                    <p className={`text-[14px] ${
                      Math.round((totalCorrect / totalQuestions) * 100) < 65
                      ? 'text-red-600'
                      : 'text-gray-600'
                    }`}>
                      Total: {totalCorrect}/{totalQuestions} ({Math.round((totalCorrect / totalQuestions) * 100)}% Accuracy)
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="h-full bg-gray-300 rounded-xl flex items-center justify-center text-gray-500 shadow-md">
              <img 
                src={case5Image} 
                alt="Cafe Bloom Case" 
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            {isNotebookOpen && hasBeenOpened && (
              <div className="w-[300px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 mt-4">
                <div className="p-3 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <h3 className="text-base font-medium text-gray-700">Research Notes</h3>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        💡 Tip: Click power to collapse notebook & click floating to reopen the notebook wherever you are in the case
                      </p>
                    </div>
                    <button 
                      className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                        hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => hasStarted && handleNotebookToggle()}
                      disabled={!hasStarted}
                      title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                      </svg>
                      {hasStarted && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                            Click to collapse notebook
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div 
                  className="notebook-content h-[300px] bg-gray-50 p-3 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                  onDragOver={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                  }}
                  onDrop={handleDrop}
                  contentEditable={hasStarted}
                  suppressContentEditableWarning={true}
                  style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                >
                  {!hasStarted && (
                    <div className="text-gray-500 text-sm italic">
                      Click "Start Case" to begin taking notes...
                    </div>
                  )}
                  {notebookItems.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 bg-white p-2 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                      contentEditable={false}
                    >
                      <div className="flex-1">
                        <div className="text-xs font-medium text-gray-900">{item.value}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{item.label}</div>
                      </div>
                      <button
                        onClick={() => hasStarted && removeNotebookItem(index)}
                        className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {hasStarted && (
            <div className="fixed top-[85px] right-10 md:right-20 z-50">
              <div 
                className={`bg-white text-[15px] font-medium px-3 py-2 rounded-lg shadow-md flex items-center gap-1.5 ${
                  timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                    ? 'animate-pulse-dramatic border-2 border-red-500'
                    : ''
                }`}
              >
                <span className={`text-base ${
                  timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                    ? 'animate-bounce'
                    : ''
                }`}>⌛</span>
                <span className={`${
                  timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                    ? 'text-red-500 font-bold'
                    : 'text-blue-600'
                }`}>
                  Time Left: {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Problem Section - Enhanced design */}
        <div id="problem" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="pt-20 pb-24 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
          <div className="absolute inset-x-0 -top-20 h-20 bg-white"></div>
          <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="md:col-span-2 relative group" style={{ 
              paddingRight: 'calc(460px + 1.25rem)',
              maxWidth: 'calc(100% - 100px)'
            }}>
              <div className="absolute -top-20 inset-x-0 bottom-0 bg-white rounded-2xl rounded-t-none shadow-xl transform group-hover:scale-[1.02] transition-transform duration-300">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-blue-50/50 opacity-50" 
                     style={{ 
                       clipPath: 'polygon(0 25%, 100% 25%, 100% 100%, 0 100%)',
                       background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.05) 50%, rgba(59, 130, 246, 0.1) 100%)'
                     }}>
                </div>
              </div>
              <div className="relative p-8">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-gray-900">
                  <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                    📝
                  </span>
                  Problem
                </h2>
                <div className="space-y-4 prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">Cafe Bloom, a popular coffee shop in the heart of downtown, has been struggling with long lines and slow service during peak morning hours. The owner recently implemented several changes: a digital ordering system, adjusted staff schedules, and an expanded breakfast menu.</p>
                  <p className="text-gray-700 leading-relaxed">After three months of these changes, the owner wants to evaluate their effectiveness in reducing congestion and improving revenue. They need to decide whether to invest more in digital ordering capabilities or hire additional baristas.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Investigation Section */}
        <div id="investigation" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-12 bg-gray-50/80">
          <div className="container pl-[72px]">
            <h2 className="text-3xl font-bold mb-3 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                🔍
              </span>
              Investigation
            </h2>
            <p className="text-sm text-gray-600 mb-8 flex items-center gap-2">
              <span className="bg-blue-50 px-2 py-0.5 rounded">💡 Tip:</span>
              Drag and drop highlighted text into your notebook to collect important data points
            </p>
            <div className="flex gap-12 pr-[200px]">
              {/* Investigation Text */}
              <div className="w-[700px]">
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative space-y-5">
                  <p className="text-lg leading-relaxed">
                    During the 6-week trial, sensors recorded over <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "2.3 million", "Total data points collected")}
                    >2.3 million</span> data points across Terminals A, B, and C. Exhibit 1 shows average wait times at security: Terminal B dropped from <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "22", "Terminal B initial security wait time")}
                    >22</span> mins to <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "11", "Terminal B final security wait time")}
                    >11</span>, while Terminal C worsened slightly from <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "16", "Terminal C initial security wait time")}
                    >16</span> to <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "18", "Terminal C final security wait time")}
                    >18</span>. Terminal A remained steady.
                  </p>
                  
                  <p className="text-lg leading-relaxed">
                    Baggage claim delays saw the biggest improvement in Terminal C — dropping from <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "28", "Terminal C initial baggage delay")}
                    >28</span> to <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "17", "Terminal C final baggage delay")}
                    >17</span> minutes. Exhibit 2 shows staffing levels per terminal: Terminal B increased staff by <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "40%", "Terminal B staff increase")}
                    >40%</span>, while Terminal C shifted staff from check-in to arrivals.
                  </p>

                  <p className="text-lg leading-relaxed">
                    Passenger feedback data (<span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "12,500", "Number of passenger surveys")}
                    >12,500</span> surveys) revealed that <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "61%", "Positive experience rating")}
                    >61%</span> of travelers rated their experience as "improved," but only <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "39%", "Gate change awareness")}
                    >39%</span> noticed the gate assignment changes — suggesting the policy had limited visibility.
                  </p>

                  <p className="text-lg leading-relaxed">
                    Flight delay rates dropped by <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "14%", "Overall delay reduction")}
                    >14%</span> overall, though terminal-specific gains were uneven: Terminal A had a <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "4%", "Terminal A delay improvement")}
                    >4%</span> improvement, B had <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "22%", "Terminal B delay improvement")}
                    >22%</span>, and C had <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "16%", "Terminal C delay improvement")}
                    >16%</span>. Boarding efficiency increased significantly in gates equipped with queue-time displays.
                  </p>

                  <p className="text-lg leading-relaxed">
                    Wi-Fi-based traffic analysis revealed congestion peaked between <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "6–9 AM", "Morning peak hours")}
                    >6–9 AM</span> and <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "5–7 PM", "Evening peak hours")}
                    >5–7 PM</span>. Passenger flow density was highest in food courts and arrival corridors. Environmental sensors indicated CO₂ levels dropped by <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "11%", "CO₂ reduction at Gate B3")}
                    >11%</span> near Gate B3, suggesting better air circulation following layout changes.
                  </p>
                </div>
              </div>

              {/* Interactive Notebook */}
              {isNotebookOpen ? (
                <div className="w-[500px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-medium text-gray-700">Research Notes</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Tip: Click power to collapse notebook & click floating button to reopen the notebook wherever you are in the case
                        </p>
                      </div>
                      <button 
                        className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                          hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => hasStarted && handleNotebookToggle()}
                        disabled={!hasStarted}
                        title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                        </svg>
                        {hasStarted && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                              Click to collapse notebook
                            </div>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                  <div 
                    className="notebook-content h-[400px] bg-gray-50 p-4 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                    onDragOver={(e) => {
                      if (!hasStarted) return;
                      e.preventDefault();
                      e.currentTarget.classList.add('bg-blue-50');
                    }}
                    onDragLeave={(e) => {
                      if (!hasStarted) return;
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-blue-50');
                    }}
                    onDrop={handleDrop}
                    contentEditable={hasStarted}
                    suppressContentEditableWarning={true}
                    style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                  >
                    {!hasStarted && (
                      <div className="text-gray-500 text-sm italic">
                        Click "Start Case" to begin taking notes...
                      </div>
                    )}
                    {notebookItems.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-2 bg-white p-2.5 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                        contentEditable={false}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{item.value}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{item.label}</div>
                        </div>
                        <button
                          onClick={() => hasStarted && removeNotebookItem(index)}
                          className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleNotebookToggle}
                  className={`fixed right-24 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transform transition-all duration-300 group z-[999] ${
                    hasStarted 
                      ? 'hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.15)] hover:scale-105'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!hasStarted}
                >
                  <div className="relative">
                    {hasStarted && (
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                    )}
                    <svg 
                      className={`w-8 h-8 ${hasStarted ? 'text-gray-600 group-hover:text-blue-600' : 'text-gray-400'} transition-colors duration-300`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  {hasStarted && (
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <div className="bg-gray-800/80 backdrop-blur-sm text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                        Open notebook ({notebookItems.length} notes collected)
                      </div>
                    </div>
                  )}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Exhibits Section */}
        <div id="exhibits" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-12 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
          <div className="absolute inset-x-0 -top-20 h-20 bg-gray-50/80"></div>
          <div className="container pl-[72px]" style={{ 
              paddingRight: 'calc(460px + 1.25rem)',
              maxWidth: 'calc(100% - 100px)'
            }}>
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                📈
              </span>
              Exhibits
            </h2>
            <p className="text-sm text-gray-600 mb-4">Select an exhibit using the navigation below to analyze the data.</p>
            
            {/* Exhibit Navigation */}
            <div className="flex items-center gap-3 mb-4">
              {['1', '2', '3', '4', '5', '6'].map((exhibit) => (
                <button
                  key={exhibit}
                  onClick={() => hasStarted && setCurrentExhibit(exhibit)}
                  disabled={!hasStarted}
                  className={`px-6 py-2 rounded-xl shadow-md transition-all duration-300 transform ${
                    !hasStarted 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                      : currentExhibit === exhibit
                        ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg hover:scale-105'
                        : 'bg-white text-gray-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105'
                  }`}
                >
                  {exhibit}
                </button>
              ))}
            </div>

            {/* Exhibit Display */}
            <div className={`bg-white rounded-xl shadow-sm p-6 transition-all duration-300 ${!hasStarted ? 'opacity-50' : 'hover:shadow-lg'}`}>
              {currentExhibit === '1' && (
                <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">🛍️</span>
                    Exhibit 1: Order Types Breakdown
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case5/case5_ex1_order_types.png')}
                      alt="Order Types Breakdown"
                      className="w-full h-full object-contain"
                    />
                </div>
                  <p className="text-sm text-gray-500 mt-4">Distribution of orders between digital and in-store channels</p>
                </>
              )}

              {currentExhibit === '2' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">⏱️</span>
                    Exhibit 2: Average Wait Time (Morning Rush)
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case5/case5_ex2_wait_time.png')}
                      alt="Average Wait Time"
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-4">Customer wait times during morning peak hours</p>
                </>
              )}

              {currentExhibit === '3' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">💰</span>
                    Exhibit 3: Monthly Revenue
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case5/case5_ex3_revenue.png')}
                      alt="Monthly Revenue"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Revenue trends over the first quarter</p>
                </>
              )}

              {currentExhibit === '4' && (
                <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-blue-600">👥</span>
                    Exhibit 4: Staff Load and Orders Served
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case5/case5_ex4_staff_orders.png')}
                      alt="Staff Load and Orders"
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-4">Daily comparison of staff hours and orders served</p>
                </>
              )}

              {currentExhibit === '5' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">🥐</span>
                    Exhibit 5: Breakfast Item Sales
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case5/case5_ex5_breakfast.png')}
                      alt="Breakfast Item Sales"
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-4">Sales breakdown by breakfast menu item</p>
                </>
              )}

              {currentExhibit === '6' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">⭐</span>
                    Exhibit 6: Customer Satisfaction by Factor
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case5/case5_ex6_satisfaction.png')}
                      alt="Customer Satisfaction"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Customer satisfaction ratings across different factors</p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Analysis Section */}
        <div id="questions" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-12 bg-gray-50/80">
          <div className="container pl-[72px]">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                🧠
              </span>
              Analysis
            </h2>
            <div className="space-y-6 md:pr-[460px]">
              {questions.map((q) => (
                <div 
                  key={q.id} 
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md">
                      {q.id}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-lg font-medium transition-all duration-300 hover:font-bold hover:text-blue-600 mb-4">
                        {q.text}
                      </h3>
                      {q.type === 'text' ? (
                        <div className="space-y-3">
                          <div className="flex gap-3">
                            <input
                              type="text"
                              value={answers[q.id] || ''}
                              onChange={(e) => handleInput(q.id, e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                              className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                              placeholder="Type your answer and press Enter..."
                              disabled={!hasStarted || timeExpired || finishedEarly}
                            />
                            <button
                              onClick={() => handleSubmit(q.id)}
                              className={`px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 ${
                                !hasStarted || timeExpired || finishedEarly ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              disabled={!hasStarted || timeExpired || finishedEarly}
                            >
                              Submit
                            </button>
                          </div>
                          <div className="flex gap-2">
                            {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                              <button
                                onClick={() => handleTryAgain(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                Try Again
                              </button>
                            )}
                            {(timeExpired || finishedEarly) && (
                              <button
                                onClick={() => handleReveal(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                See Answer
                              </button>
                            )}
                          </div>
                          {feedback[q.id] && (
                            <div className={`mt-4 p-3 rounded-lg ${
                              feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                            </div>
                          )}
                          {reveal[q.id] && (
                            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                              <p className="text-blue-700">🔍 Answer: <strong>{q.answer}</strong></p>
                              <p className="text-sm text-blue-600 mt-1">Hint: {q.hint}</p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}

              {finishedEarly && (
                <div className="mt-6 text-green-600 text-base font-semibold">
                  🎉 Congratulations! You completed all questions before the timer ran out! Amazing work!
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Report Section */}
        <div id="report" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-20 bg-gradient-to-br from-blue-50/40 via-blue-50/20 to-white">
          <div className="container pl-[72px]">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                📊
              </span>
              Report
            </h2>
            <div className="space-y-6 md:pr-[460px]">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative">
                <div className="flex items-start gap-4">
                  <div className="flex-grow">
                    <div className="space-y-3">
                      <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                        Digital orders now represent <input
                          type="text"
                          value={answers['report_digital_percent'] || ''}
                          onChange={(e) => handleInput('report_digital_percent', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_digital_percent'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_digital_percent'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> of total sales, with peak congestion dropping significantly by <input
                          type="text"
                          value={answers['report_peak_time'] || ''}
                          onChange={(e) => handleInput('report_peak_time', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_peak_time'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_peak_time'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> AM. Revenue grew <input
                          type="text"
                          value={answers['report_revenue_growth'] || ''}
                          onChange={(e) => handleInput('report_revenue_growth', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_revenue_growth'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_revenue_growth'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> between January and March. Staff productivity was highest on <input
                          type="text"
                          value={answers['report_productive_day'] || ''}
                          onChange={(e) => handleInput('report_productive_day', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_productive_day'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_productive_day'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        />, suggesting targeted coverage is working. Muffins were the most popular item with <input
                          type="text"
                          value={answers['report_muffins'] || ''}
                          onChange={(e) => handleInput('report_muffins', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_muffins'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_muffins'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> sold, and customer satisfaction scored highest for <input
                          type="text"
                          value={answers['report_satisfaction'] || ''}
                          onChange={(e) => handleInput('report_satisfaction', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_satisfaction'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_satisfaction'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        />. The lowest traffic occurred on <input
                          type="text"
                          value={answers['report_lowest_day'] || ''}
                          onChange={(e) => handleInput('report_lowest_day', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_lowest_day'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_lowest_day'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        />, indicating an opportunity for weekday promotions.
                      </div>
                      <div className="flex gap-2">
                        {(!timeExpired && !finishedEarly) && (
                          <button
                            onClick={() => handleReportSubmit()}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit Answers
                          </button>
                        )}
                        {(timeExpired || finishedEarly) && (
                          <button
                            onClick={() => handleReportReveal()}
                            className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                          >
                            See Answers
                          </button>
                        )}
                      </div>
                      {(timeExpired || finishedEarly) && feedback['report'] && (
                        <div className={`mt-4 p-3 rounded-lg ${
                          Object.values(feedback).every(f => f === 'correct') 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {Object.values(feedback).every(f => f === 'correct') 
                            ? 'All answers are correct!' 
                            : 'Some answers are incorrect. Please check your answers.'}
                        </div>
                      )}
                      {reveal['report'] && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                          <p className="text-blue-700">
                            🔍 Answers:<br/>
                            Digital Orders: 40%<br/>
                            Peak Time: 9 AM<br/>
                            Revenue Growth: 28.8%<br/>
                            Most Productive Day: Friday<br/>
                            Muffins Sold: 70<br/>
                            Highest Satisfaction: Freshness<br/>
                            Lowest Traffic Day: Monday
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {timeExpired && !finishedEarly && (
                <div className="mt-6 text-red-600 text-base font-semibold">
                  ❌ Time's up! You can still view the answers by clicking "See Answer".
                </div>
              )}
              {finishedEarly && timeExpired && (
                <div className="mt-6 text-green-600 text-base font-semibold">
                  🎉 Congratulations! You completed the report before the timer ran out!
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Recommendation Section - Subtle off-white */}
        <div id="recommendation" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-20 bg-gray-50/80">
          <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="md:col-span-2 md:pr-[460px]">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  💡
                </span>
                Recommendation
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="mr-3 text-xl">📊</span>
                  <span>Roll out dynamic queue displays airport-wide</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-xl">👥</span>
                  <span>Focus staffing adjustments on peak windows and congested terminals</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-xl">🎯</span>
                  <span>Revise gate assignment policy to be more visible to travelers</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-xl">♻️</span>
                  <span>Expand ventilation improvements to other high-density gates</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Case5Page; 

<style jsx global>{`
    @keyframes pulse-dramatic {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
      }
      50% {
        transform: scale(1.15);
        box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
      }
    }
    
    .animate-pulse-dramatic {
      animation: pulse-dramatic 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0) scale(1);
      }
      50% {
        transform: translateY(-5px) scale(1.2);
      }
    }

    .animate-bounce {
      animation: bounce 0.5s ease infinite;
    }
  `}</style>